<div *ngIf="translationsReady" class="flex flex-col h-full max-h-[85vh]" [dir]="isRTL ? 'rtl' : 'ltr'">
  <!-- Header -->
  <div class="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white shrink-0">
    <div>
      <h2 class="text-base font-bold text-gray-800">
        {{ 'CONTACT_UPLOAD_BATCH.MODAL_TITLE' | translate }}
      </h2>
      <p class="text-[11px] text-gray-400 mt-0.5 font-mono">
        {{ batchData.batchReference }}
      </p>
    </div>
    <button
      type="button"
      (click)="close()"
      class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none"
    >
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>

  <!-- Status & Summary -->
  <div class="px-5 py-3 border-b border-gray-100 bg-gray-50/50 shrink-0">
    <div class="flex flex-wrap items-center gap-2 mb-2">
      <span
        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wide border"
        [ngClass]="statusConfig.class"
      >
        <i *ngIf="!statusConfig.spinner" class="fas" [ngClass]="statusConfig.icon"></i>
        <div *ngIf="statusConfig.spinner" class="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
        <ng-container [ngSwitch]="batchData.status">
          <span *ngSwitchCase="'COMPLETED'">{{ "CONTACT_UPLOAD_BATCH.STATUS.COMPLETED" | translate }}</span>
          <span *ngSwitchCase="'PARTIAL'">{{ "CONTACT_UPLOAD_BATCH.STATUS.PARTIAL" | translate }}</span>
          <span *ngSwitchCase="'FAILED'">{{ "CONTACT_UPLOAD_BATCH.STATUS.FAILED" | translate }}</span>
          <span *ngSwitchCase="'PROCESSING'">{{ "CONTACT_UPLOAD_BATCH.STATUS.PROCESSING" | translate }}</span>
          <span *ngSwitchDefault>{{ "CONTACT_UPLOAD_BATCH.STATUS.PENDING" | translate }}</span>
        </ng-container>
      </span>
      <span *ngIf="batchData.completedAt" class="text-[11px] text-gray-400 font-medium">
        {{ 'CONTACT_UPLOAD_BATCH.COMPLETED_AT' | translate }}: {{ batchData.completedAt | date:'dd/MM/yyyy HH:mm' }}
      </span>
      <span *ngIf="!batchData.completedAt && batchData.startedAt" class="text-[11px] text-gray-400 font-medium">
        {{ 'CONTACT_UPLOAD_BATCH.STARTED_AT' | translate }}: {{ batchData.startedAt | date:'dd/MM/yyyy HH:mm' }}
      </span>
    </div>

    <div class="flex flex-wrap gap-x-4 gap-y-1 text-[12px] font-semibold text-gray-600">
      <span>
        <span class="text-gray-400 font-normal">{{ "CONTACT_UPLOAD_BATCH.SUMMARY.TOTAL" | translate }}:</span>
        {{ batchData.totalCount ?? 0 }}
      </span>
      <span>
        <span class="text-gray-400 font-normal">{{ "CONTACT_UPLOAD_BATCH.SUMMARY.INSERTED" | translate }}:</span>
        {{ batchData.mainInsertedCount ?? 0 }}
      </span>
      <span>
        <span class="text-gray-400 font-normal">{{ "CONTACT_UPLOAD_BATCH.SUMMARY.UPDATED" | translate }}:</span>
        {{ batchData.mainUpdatedCount ?? 0 }}
      </span>
      <span>
        <span class="text-gray-400 font-normal">{{ "CONTACT_UPLOAD_BATCH.SUMMARY.FAILED" | translate }}:</span>
        {{ notImportedCount }}
      </span>
    </div>

    <p *ngIf="guidanceKey" class="mt-2 text-[12px] leading-snug text-gray-600" role="status">
      {{ guidanceKey | translate }}
    </p>
  </div>

  <!-- Failed Contacts List -->
  <div class="flex-1 overflow-y-auto px-5 py-3 min-h-0">
    <div *ngIf="batchData.stagingFailureContacts?.length" class="space-y-3">
      <h3 class="text-[13px] font-bold text-gray-700">
        {{ "CONTACT_UPLOAD_BATCH.FAILED_CONTACTS" | translate }}
        <span class="text-red-600">({{ notImportedCount }})</span>
      </h3>

      <div class="overflow-x-auto rounded-lg border border-gray-200">
        <table class="w-full text-[12px]">
          <thead class="bg-gray-50 text-gray-500 font-bold uppercase tracking-wider">
            <tr>
              <th class="px-3 py-2 text-left">{{ "CONTACT_UPLOAD_BATCH.TABLE.PHONE_NUMBER" | translate }}</th>
              <th class="px-3 py-2 text-left">{{ "CONTACT_UPLOAD_BATCH.TABLE.NAME" | translate }}</th>
              <th class="px-3 py-2 text-left">{{ "CONTACT_UPLOAD_BATCH.TABLE.FIRST_NAME" | translate }}</th>
              <th class="px-3 py-2 text-left">{{ "CONTACT_UPLOAD_BATCH.TABLE.GENDER" | translate }}</th>
              <th class="px-3 py-2 text-left">{{ "CONTACT_UPLOAD_BATCH.TABLE.ERROR_REASON" | translate }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr *ngFor="let item of batchData.stagingFailureContacts" class="hover:bg-gray-50/50">
              <td class="px-3 py-2 font-medium text-gray-700">{{ item.contact?.phoneNumber ?? '-' }}</td>
              <td class="px-3 py-2 text-gray-600">{{ item.contact?.name ?? '-' }}</td>
              <td class="px-3 py-2 text-gray-600">{{ item.contact?.firstName ?? '-' }}</td>
              <td class="px-3 py-2 text-gray-600">{{ item.contact?.gender ?? '-' }}</td>
              <td class="px-3 py-2 text-red-600 font-medium">{{ translateReason(item.reason) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Download Button -->
      <div class="flex justify-end pt-1">
        <button
          type="button"
          (click)="downloadFailedContacts()"
          [disabled]="downloading"
          class="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-[12px] font-bold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i *ngIf="!downloading" class="fas fa-download"></i>
          <div *ngIf="downloading" class="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
          {{ "CONTACT_UPLOAD_BATCH.DOWNLOAD_FAILED" | translate }}
        </button>
      </div>
    </div>

    <!-- Empty State (no failures) -->
    <div *ngIf="!batchData.stagingFailureContacts?.length" class="py-8 text-center">
      <div class="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-50 text-green-500 mb-3">
        <i class="fas fa-check text-lg"></i>
      </div>
      <p class="text-[12px] font-semibold text-gray-500">
        {{ "CONTACT_UPLOAD_BATCH.ALL_SUCCESS" | translate }}
      </p>
    </div>
  </div>
</div>
